import { Auth } from '@aws-amplify/auth';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faCogs } from '@fortawesome/free-solid-svg-icons';
import React, { useContext, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { HiOutlineCloudDownload as DownloadIcon } from 'react-icons/hi';

import { userTypes } from '../utils/enums';
import { ReactComponent as FinancesIcon } from '../image/finances-icon.svg';
import { ReactComponent as ListIcon } from '../image/list.svg';
import { ReactComponent as LogOutIcon } from '../image/log-out-icon.svg';
import { ReactComponent as MyInfoIcon } from '../image/my-info-icon.svg';
import { ReactComponent as LNHLogo } from '../image/pcg-scholar-stacked.svg';
import { ReactComponent as TSBLogo } from '../image/pcg-tsb-stacked.svg';
// TODO If Reports becomes its own page again in 1.1.0+ this will be needed.
// import { ReactComponent as ReportIcon } from "../image/report-icon.svg";
import { ReactComponent as UniversityIcon } from '../image/university-icon.svg';
import { ReactComponent as UserSettingsIcon } from '../image/user-settings-icon.svg';
import { ProtectedRouteContext, ShipyardContext } from './Context';
import Button from './shared/Button';

import './NavBar.scss';
import { Box, Dialog, DialogContent } from '@mui/material';

function NavBar() {
    const history = useHistory();
    const location = useLocation();
    const path = location.pathname.slice(1);
    const [t] = useTranslation();
    const { userSettings, updateUserSettings, resetValue } = useContext(ShipyardContext);
    const { displayModal, warning, setWarning } = useContext(ProtectedRouteContext);

    const [expanded, setExpanded] = useState(false);
    const [selected, setSelected] = useState(path);
    const [delayedNavigate, setDelayedNavigate] = useState(null);

    const signOut = () => {
        window.sessionStorage.clear();
        Auth.signOut();
        updateUserSettings((prev) => {
            let clientSettings = {
                ClientOrgId: prev.ClientOrgId,
                ClientHeaderImage: prev.ClientHeaderImage,
                ClientHeaderText: prev.ClientHeaderText,
                ClientName: prev.ClientName,
            };
            return { ...resetValue, ...clientSettings };
        });
        history.push('/signin');
        setExpanded(false);
    };

    const navigate = (navigateTo) => {
        if (!!warning) {
            let param2 = () => (
                <Dialog open={true}>
                    <DialogContent>
                        <Box>{warning}</Box>
                        <Box>
                            <Button
                                useMui={true}
                                type="negative"
                                onClick={() => {
                                    displayModal(false, null);
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                useMui={true}
                                onClick={() => {
                                    displayModal(false, null);
                                    setWarning(null);
                                    setDelayedNavigate(navigateTo);
                                }}
                            >
                                Continue
                            </Button>
                        </Box>
                    </DialogContent>
                </Dialog>
            );
            displayModal(true, param2);
            return;
        }

        history.push('/' + navigateTo);
        setExpanded(false);
        setSelected(navigateTo);
    };

    const toggleMobileExpansion = () => {
        setExpanded(!expanded);
    };

    useEffect(() => {
        if (!!delayedNavigate && warning === null) {
            history.push('/' + delayedNavigate);
            setSelected(delayedNavigate);
            setExpanded(false);
        }
    }, [warning, delayedNavigate, history]);

    //TODO #Phase2 Dashboard to be implemented at a later date
    // const dashboard =
    //     <li
    //         onClick={() => {navigate("dashboard")}}
    //         className={selected === "dashboard" || selected === "" ? "selected" : null}
    //         key="li-dashboard"
    //     >
    //         <DashboardIcon />
    //         <span>{t("NavBar.Dashboard")}</span>
    //     </li>;

    const applications = (
        <li
            onClick={() => {
                navigate('applications');
            }}
            className={selected === 'applications' ? 'selected' : null}
            key="li-applications"
        >
            <ListIcon />
            <span>{t('NavBar.Applications')}</span>
        </li>
    );

    const students = (
        <li
            onClick={() => {
                navigate('students');
            }}
            className={selected === 'students' ? 'selected' : null}
            key="li-students"
        >
            <ListIcon />
            <span>{t('NavBar.Students')}</span>
        </li>
    );

    const finances = (
        <li
            onClick={() => {
                navigate('finances');
            }}
            className={selected === 'finances' ? 'selected' : null}
            key="li-finances"
        >
            <FinancesIcon />
            <span>{t('NavBar.Finances')}</span>
        </li>
    );

    // TODO 1.1.0+ This may be a page, for now it will be an action on the Record view.
    // const reports =
    //     <li
    //         onClick={() => {navigate("reports")}}
    //         className={selected === "reports" ? "selected" : null}
    //         key="li-reports"
    //     >
    //         <ReportIcon />
    //         <span>{t("NavBar.Reports")}</span>
    //     </li>;

    const settings = (
        <li
            onClick={() => {
                navigate('settings');
            }}
            className={selected === 'settings' ? 'selected' : null}
            key="li-settings"
        >
            <UserSettingsIcon />
            <span>{t('NavBar.Settings')}</span>
        </li>
    );

    const siteSettings = (
        <li
            onClick={() => {
                navigate('sitesettings');
            }}
            className={selected === 'sitesettings' ? 'selected' : null}
            key="li-sitesettings"
        >
            <FontAwesomeIcon icon={faCogs} />
            <span>{t('NavBar.SiteSettings')}</span>
        </li>
    );

    const myInfo = (
        <li
            onClick={() => {
                navigate('usersettings');
            }}
            className={selected === 'usersettings' ? 'selected' : null}
            key="li-usersettings"
        >
            <MyInfoIcon />
            <span>{t('NavBar.UserSettings')}</span>
        </li>
    );

    const schoolInfo = (
        <li
            onClick={() => {
                navigate('schoolinfo');
            }}
            className={selected === 'schoolinfo' ? 'selected' : null}
            key="li-schoolinfo"
        >
            <UniversityIcon />
            <span>School Info</span>
        </li>
    );

    const navContents = [];
    switch (userSettings?.UserType) {
        case userTypes.Parent:
            navContents.push(applications, myInfo);
            break;
        case userTypes.PrivateSchool:
            navContents.push(students, finances, schoolInfo, myInfo);
            break;
        case userTypes.ScholarshipOrg:
            //#TODO TechDebt - Grab the set of nav items from the database based on client/usertype combination.
            userSettings?.ClientOrgId !== 7
                ? navContents.push(
                      applications,
                      finances,
                      <li
                          onClick={() => {
                              navigate('downloads');
                          }}
                          className={selected === 'downloads' ? 'selected' : null}
                          key="li-downloads"
                      >
                          <DownloadIcon
                              className="no-style"
                              style={{
                                  padding: '1em 1em 0 1em',
                              }}
                          />
                          <span>Downloads</span>
                      </li>
                  )
                : navContents.push(applications);
            navContents.push(settings);
            break;
        case userTypes.Admin:
            navContents.push(siteSettings, myInfo);
            break;
        default:
            break;
    }

    return (
        <nav
            id="nav-bar"
            aria-label="Main"
            className={expanded ? 'expanded' : ''}
        >
            <h1>{userSettings.ClientOrgId === 5 ? <LNHLogo /> : <TSBLogo />}</h1>
            <div
                className="expando"
                onClick={toggleMobileExpansion}
            >
                <FontAwesomeIcon icon={faBars} />
            </div>
            <ul>{navContents}</ul>
            <div className="log-out">
                <button onClick={signOut}>
                    <LogOutIcon />
                    <span>{t('NavBar.LogOut')}</span>
                </button>
            </div>
        </nav>
    );
}

export default NavBar;

// TODO: ask blake to regenerate finances icon with $ as path
// TODO: mobile view
